import classnames from 'classnames';
import { useEffect, useState } from "react";
import { MutatingDots } from "react-loader-spinner";
import {
  Badge,
  Button,
  Card, CardBody,
  Col, Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalHeader,
  Nav, NavItem, NavLink,
  Row,
  TabContent,
  Table,
  TabPane
} from "reactstrap";
import * as XLSX from 'xlsx';
import noimage from "../../assets/images/noimage.png";
import api from "../../services/api";


const CourseCards = () => {
const backendUrl = process.env.REACT_APP_BACKEND_URL;
 const backendFileUrl = process.env.REACT_APP_BACKEND_IMAGE_URL;
  const [courses, setCourses] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedPassoutYear, setSelectedPassoutYear] = useState(null);
  const [passoutYears, setPassoutYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [coursesLoader, setCoursesLoader] = useState(true);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [termsDropdownOpen, setTermsDropdownOpen] = useState({});
  const [technologiesLoading, setTechnologiesLoading] = useState(false);
  const [selectedTerms, setSelectedTerms] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [paymentLoading, setPaymentLoading] = useState({});
  const [activeTab, setActiveTab] = useState({});
  const [paidUnpaidCounts, setPaidUnpaidCounts] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTechData, setSelectedTechData] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');

  // New states for overall course tab
  const [mainActiveTab, setMainActiveTab] = useState('technologies');
  const [overallTerms, setOverallTerms] = useState([]);
  const [selectedOverallTerm, setSelectedOverallTerm] = useState('');
  const [overallPaymentData, setOverallPaymentData] = useState(null);
  const [overallPaymentLoading, setOverallPaymentLoading] = useState(false);
  const [overallDropdownOpen, setOverallDropdownOpen] = useState(false);

  const toggleYearDropdown = () => setYearDropdownOpen(prev => !prev);
  const toggleTermsDropdown = (techId) => {
    setTermsDropdownOpen(prev => ({
      ...prev,
      [techId]: !prev[techId]
    }));
  };

  const toggleOverallDropdown = () => setOverallDropdownOpen(prev => !prev);

  const toggleModal = () => setModalOpen(!modalOpen);

  useEffect(() => {
    fetchActiveCourses();
  }, []);

  const fetchActiveCourses = async () => {
    try {
      const response = await api.get(`${backendUrl}get-active-courses`);
      if (response.status === 200) {
        setCourses(response.data.data);
        setCoursesLoader(false);
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      setCoursesLoader(false);
    }
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setSelectedPassoutYear(null);
    setTechnologies([]);
    setSelectedTerms({});
    setPaymentData({});
    setPaidUnpaidCounts({});
    setOverallPaymentData(null);
    setSelectedOverallTerm('');

    // Generate passout years (current year to +5 years)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => currentYear + i);
    setPassoutYears(years);
  };

  const handlePassoutYearSelect = async (year) => {
    setSelectedPassoutYear(year);
    setTechnologiesLoading(true);

    try {
      const response = await api.post(
        `${backendUrl}get-technologies-by-course-and-passout-year`,
        {
          course: selectedCourse._id,
          passout_year: year
        }
      );

      if (response.status === 200) {
        const technologiesData = response.data.data || [];
        setTechnologies(technologiesData);

        // Get all unique terms from all technologies for overall tab
        const allTerms = [...new Set(technologiesData.flatMap(tech => tech.terms))];
        setOverallTerms(allTerms);

        // Initialize selected terms, active tabs, and counts for each technology
        const initialTerms = {};
        const initialTabs = {};
        const initialCounts = {};

        technologiesData.forEach(tech => {

          const defaultTerm = tech.terms.includes("Select Term") ? "Select Term" : (tech.terms[0] || "No terms available");
          initialTerms[tech.technology_id] = defaultTerm;
          initialTabs[tech.technology_id] = 'paid';
          initialCounts[tech.technology_id] = {
            paid: 0,
            unpaid: 0
          };
        });

        setSelectedTerms(initialTerms);
        setActiveTab(initialTabs);
        setPaidUnpaidCounts(initialCounts);

        // Fetch payment data for all technologies with their default terms
        technologiesData.forEach(tech => {
          const defaultTerm = initialTerms[tech.technology_id];
          if (defaultTerm && defaultTerm !== "No terms available") {
            fetchPaymentData(tech.technology_id, defaultTerm);
          }
        });
      }
    } catch (err) {
      console.error("Error fetching technologies:", err);
      setTechnologies([]);
      setOverallTerms([]);
    } finally {
      setTechnologiesLoading(false);
    }
  };

  const handleTermSelect = async (techId, term) => {
    setSelectedTerms(prev => ({
      ...prev,
      [techId]: term
    }));

    // Fetch payment data when term is selected
    await fetchPaymentData(techId, term);
  };

  const fetchPaymentData = async (techId, term) => {
    setPaymentLoading(prev => ({ ...prev, [techId]: true }));

    try {
      const response = await api.post(
        `${backendUrl}get-student-payment-status`,
        {
          course: selectedCourse._id,
          passout_year: selectedPassoutYear,
          technology: techId,
          term: term
        }
      );
      console.log(response.data);


      if (response.status === 200) {
        setPaymentData(prev => ({
          ...prev,
          [techId]: response.data.data
        }));

        // Update paid/unpaid counts based on actual API response
        if (response.data.data && response.data.data.terms && response.data.data.terms.length > 0) {
          const termData = response.data.data.terms[0];
          const paidCount = termData.paid_students?.count || 0;
          const unpaidCount = termData.unpaid_students?.count || 0;

          setPaidUnpaidCounts(prev => ({
            ...prev,
            [techId]: {
              paid: paidCount,
              unpaid: unpaidCount
            }
          }));
        } else {
          // If no data found, set counts to 0
          setPaidUnpaidCounts(prev => ({
            ...prev,
            [techId]: {
              paid: 0,
              unpaid: 0
            }
          }));
        }
      }
    } catch (err) {
      console.error("Error fetching payment data:", err);
      // Set counts to 0 on error
      setPaidUnpaidCounts(prev => ({
        ...prev,
        [techId]: {
          paid: 0,
          unpaid: 0
        }
      }));
    } finally {
      setPaymentLoading(prev => ({ ...prev, [techId]: false }));
    }
  };

  const handleOverallTermSelect = async (term) => {
    setSelectedOverallTerm(term);
    setOverallPaymentLoading(true);

    try {
      const response = await api.post(
        `${backendUrl}get-student-payments-overall`,
        {
          course: selectedCourse._id,
          passout_year: selectedPassoutYear,
          term: term
        }
      );


      console.log(selectedCourse._id, selectedPassoutYear, term);
      console.log(response);





      if (response.status === 200 && response.data.success) {
        setOverallPaymentData(response.data.data);
      } else {
        setOverallPaymentData(null);
      }
    } catch (err) {
      console.error("Error fetching overall payment data:", err);
      setOverallPaymentData(null);
    } finally {
      setOverallPaymentLoading(false);
    }
  };

  const handleTabChange = (techId, tabName) => {
    setActiveTab(prev => ({
      ...prev,
      [techId]: tabName
    }));
  };

  const handleViewFullScreen = (tech, type) => {
    setSelectedTechData({
      tech,
      paymentData: paymentData[tech.technology_id],
      type
    });
    setSelectedPaymentType(type);
    toggleModal();
  };

  const handleOverallViewFullScreen = (type) => {
    setSelectedTechData({
      tech: { technology_name: 'Overall Course' },
      paymentData: overallPaymentData,
      type
    });
    setSelectedPaymentType(type);
    toggleModal();
  };

  const downloadExcel = () => {
    if (!selectedTechData) return;

    const { tech, paymentData, type } = selectedTechData;

    // Check if it's overall course data or technology-specific data
    const isOverall = tech.technology_name === 'Overall Course';

    let excelData = [];
    let fileName = '';

    if (isOverall) {
      // Overall course data - corrected structure
      const technologiesData = paymentData.technologies || [];
      let students = [];

      if (technologiesData.length > 0) {
        students = type === 'paid'
          ? technologiesData[0].paid_students || []
          : technologiesData[0].unpaid_students || [];
      }

      if (students.length === 0) {
        alert('No data to download');
        return;
      }

      excelData = students.map((student, index) => ({
        'S.No': index + 1,
        'Roll No': student.roll_no || 'N/A',
        'Name': student.first_name || 'N/A',
        'College': student.college || 'N/A',
        'Branch': Array.isArray(student.branch) ? student.branch.join(', ') : student.branch || 'N/A',
        'Email': student.email || 'N/A',
        'Mobile': student.mobile || 'N/A',
        'Gender': student.gender || 'N/A',
        'Technology': student.technology_name || 'N/A',
        ...(type === 'paid' && {
          'Amount': student.amount || '0',
          // 'Payment ID': student.payment_id || 'N/A',
          'Paid On': student.paid_on || 'N/A',
          // 'Remarks': student.remarks || 'N/A'
        }),
        'Course': paymentData.course || 'N/A',
        'Passout Year': paymentData.passout_year || 'N/A',
        'Term': paymentData.term || 'N/A'
      }));

      fileName = `Overall_${type}_${paymentData.term}_${paymentData.passout_year}.xlsx`;
    } else {
      // Technology-specific data
      const termData = paymentData.terms && paymentData.terms[0];
      const students = termData ? (type === 'paid' ? termData.paid_students : termData.unpaid_students) : null;

      if (!students || students.count === 0 || !students.students || students.students.length === 0) {
        alert('No data to download');
        return;
      }

      if (type === 'paid' && students.students && students.students.length > 0) {
        // For paid students with detailed data
        excelData = students.students.map((student, index) => ({
          'S.No': index + 1,
          'Roll No': student.roll_no || 'N/A',
          'Name': student.first_name || 'N/A',
          'College': student.college || 'N/A',
          'Branch': Array.isArray(student.branch) ? student.branch.join(', ') : student.branch || 'N/A',
          'Email': student.email || 'N/A',
          'Mobile': student.mobile || 'N/A',
          'Gender': student.gender || 'N/A',
          'Amount': student.amount || '0',
          // 'Payment ID': student.payment_id || 'N/A',
          'Paid On': student.paid_on || 'N/A',
          // 'Remarks': student.remarks || 'N/A',
          'Course': paymentData.course || 'N/A',
          'Technology': paymentData.technology || 'N/A',
          'Passout Year': paymentData.passout_year || 'N/A',
          'Term': paymentData.term_name || 'N/A'
        }));
      } else if (type === 'unpaid' && students.students && students.students.length > 0) {
        // For unpaid students
        excelData = students.students.map((student, index) => ({
          'S.No': index + 1,
          'Roll No': student.roll_no || 'N/A',
          'Name': student.first_name || 'N/A',
          'College': student.college || 'N/A',
          'Branch': Array.isArray(student.branch) ? student.branch.join(', ') : student.branch || 'N/A',
          'Email': student.email || 'N/A',
          'Mobile': student.mobile || 'N/A',
          'Gender': student.gender || 'N/A',
          'Course': paymentData.course || 'N/A',
          'Technology': paymentData.technology || 'N/A',
          'Passout Year': paymentData.passout_year || 'N/A',
          'Term': paymentData.term_name || 'N/A',
          'Status': 'Unpaid'
        }));
      }

      fileName = `${tech.technology_name}_${type}_${selectedTerms[tech.technology_id]}_${selectedPassoutYear}.xlsx`;
    }

    if (excelData.length === 0) {
      alert('No data to download');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Student Data');

    XLSX.writeFile(workbook, fileName);
  };

  const renderStudentTable = (techId, type) => {
    const data = paymentData[techId];
    if (!data || !data.terms || data.terms.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No {type} students found</p>
        </div>
      );
    }

    const termData = data.terms[0];
    const students = type === 'paid' ? termData.paid_students : termData.unpaid_students;

    if (!students || students.count === 0 || !students.students || students.students.length === 0) {
      return (
        <div className="text-center py-4">
          <p className="text-muted">No {type} students found</p>
        </div>
      );
    }

    // Show limited data in card and full data in modal
    const displayStudents = students.students.slice(0, 3); // Show only first 3 students in card

    return (
      <div>
        <div className="table-responsive" style={{ maxHeight: '300px' }}>
          <Table striped bordered hover size="sm">
            <thead className="table-dark">
              <tr>
                <th>S.No</th>
                <th>Name</th>
                <th>Roll No</th>
                <th>College</th>
                {type === 'paid' && <th>Amount</th>}
              </tr>
            </thead>
            <tbody>
              {displayStudents.map((student, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td className="fw-bold">{student.first_name || 'N/A'}</td>
                  <td>{student.roll_no || 'N/A'}</td>
                  <td>{student.college || 'N/A'}</td>
                  {type === 'paid' && (
                    <td className="fw-bold text-success">₹{student.amount || '0'}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {/* Show "View More" text if there are more than 3 students */}
        {students.students.length > 3 && (
          <div className="text-center mt-2">
            <Badge color="info">
              +{students.students.length - 3} more students
            </Badge>
          </div>
        )}

        {/* Show View Full Screen button only when there is data */}
        <div className="text-end mt-3">
          <Button
            color="primary"
            size="sm"
            onClick={() => handleViewFullScreen(
              technologies.find(t => t.technology_id === techId),
              type
            )}
          >
            <i className="fas fa-expand me-2"></i>
            View Full Screen
          </Button>
        </div>
      </div>
    );
  };

  const renderOverallCourseTab = () => {
    // Calculate counts for overall data - corrected structure
    const technologiesData = overallPaymentData?.technologies || [];
    let paidCount = 0;
    let unpaidCount = 0;

    if (technologiesData.length > 0) {
      paidCount = technologiesData[0].paid_students?.length || 0;
      unpaidCount = technologiesData[0].unpaid_students?.length || 0;
    }

    return (
      <Card className="shadow border-3">
        <CardBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-bold mb-0" style={{ color: "#0b0b0bff" }}>
              Overall Course Payment
            </h3>
            <Badge color="primary" className="fs-6">
              {selectedCourse?.course_name} - {selectedPassoutYear}
            </Badge>
          </div>

          {/* Term Selection for Overall Course */}
          <div className="mb-4">
            <label className="form-label fw-bold text-muted mb-2">Select Term:</label>
            <Dropdown
              isOpen={overallDropdownOpen}
              toggle={toggleOverallDropdown}
            >
              <DropdownToggle
                caret
                className="w-100 py-2 fw-bold border-0"
                style={{
                  background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                  color: "#2c3e50",
                  borderRadius: "8px",
                  border: "1px solid #dee2e6",
                  maxWidth: "300px"
                }}
              >
                {selectedOverallTerm || "Select Term"}
              </DropdownToggle>
              <DropdownMenu>
                {overallTerms.map((term, index) => (
                  <DropdownItem
                    key={index}
                    onClick={() => handleOverallTermSelect(term)}
                    className="fw-bold"
                  >
                    {term}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>

          {/* Overall Payment Counts */}
          {selectedOverallTerm && (
            <div className="mb-4">
              {overallPaymentLoading ? (
                <div className="text-center py-4">
                  <MutatingDots
                    height="60"
                    width="60"
                    color="#667eea"
                    secondaryColor="#667eea"
                    radius="12.5"
                    ariaLabel="mutating-dots-loading"
                  />
                  <p className="mt-2 text-muted">Loading payment data...</p>
                </div>
              ) : overallPaymentData ? (
                <Row className="g-4">
                  <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody className="text-center p-4">
                        <div className="p-3 rounded" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
                          <h2 className="text-success fw-bold mb-2">
                            {paidCount}
                          </h2>
                          <h5 className="text-muted fw-bold mb-3">Paid Students</h5>
                          <Button
                            color="success"
                            onClick={() => handleOverallViewFullScreen('paid')}
                            disabled={paidCount === 0}
                          >
                            <i className="fas fa-eye me-2"></i>
                            View Data
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 shadow-sm h-100">
                      <CardBody className="text-center p-4">
                        <div className="p-3 rounded" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                          <h2 className="text-danger fw-bold mb-2">
                            {unpaidCount}
                          </h2>
                          <h5 className="text-muted fw-bold mb-3">Unpaid Students</h5>
                          <Button
                            color="danger"
                            onClick={() => handleOverallViewFullScreen('unpaid')}
                            disabled={unpaidCount === 0}
                          >
                            <i className="fas fa-eye me-2"></i>
                            View Data
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No payment data available for the selected term.</p>
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    );
  };

  const renderFullScreenTable = () => {
    if (!selectedTechData) return null;

    const { tech, paymentData, type } = selectedTechData;
    const isOverall = tech.technology_name === 'Overall Course';

    let students = [];
    let totalCount = 0;

    if (isOverall) {
      // Overall course data - corrected structure
      const technologiesData = paymentData.technologies || [];
      if (technologiesData.length > 0) {
        students = type === 'paid'
          ? technologiesData[0].paid_students || []
          : technologiesData[0].unpaid_students || [];
      }
      totalCount = students.length;
    } else {
      // Technology-specific data
      const termData = paymentData.terms && paymentData.terms[0];
      students = termData ? (type === 'paid' ? termData.paid_students?.students : termData.unpaid_students?.students) : [];
      totalCount = termData ? (type === 'paid' ? termData.paid_students?.count : termData.unpaid_students?.count) : 0;
    }

    return (
      <Modal isOpen={modalOpen} toggle={toggleModal} size="xl" style={{ maxWidth: '95vw' }}>
        <ModalHeader toggle={toggleModal}>
          <div>
            <h4 className="mb-1">
              {type === 'paid' ? 'Paid' : 'Unpaid'} Students - {tech.technology_name}
            </h4>
            <small className="text-muted">
              {selectedCourse?.course_name} | {selectedPassoutYear} | {isOverall ? paymentData.term : selectedTerms[tech.technology_id]}
            </small>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <Badge color={type === 'paid' ? 'success' : 'danger'} className="fs-6">
              Total {type} Students: {totalCount}
            </Badge>
            <Button color="success" onClick={downloadExcel}>
              <i className="fas fa-download me-2"></i>
              Download Excel
            </Button>
          </div>

          {students && students.length > 0 ? (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead className="table-dark">
                  <tr>
                    <th>S.No</th>
                    <th>Roll No</th>
                    <th>Name</th>
                    <th>College</th>
                    <th>Branch</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Gender</th>
                    {isOverall && <th>Technology</th>}
                    {type === 'paid' && (
                      <>
                        <th>Amount</th>
                        <th>Payment ID</th>
                        <th>Paid On</th>
                        {/* <th>Remarks</th> */}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{student.roll_no || 'N/A'}</td>
                      <td className="fw-bold">{student.first_name || 'N/A'}</td>
                      <td>{student.college || 'N/A'}</td>
                      <td>
                        {Array.isArray(student.branch)
                          ? student.branch.join(', ')
                          : student.branch || 'N/A'
                        }
                      </td>
                      <td>{student.email || 'N/A'}</td>
                      <td>{student.mobile || 'N/A'}</td>
                      <td>
                        <Badge
                          color={student.gender === 'female' ? 'danger' : 'primary'}
                        >
                          {student.gender || 'N/A'}
                        </Badge>
                      </td>
                      {isOverall && (
                        <td>{student.technology_name || 'N/A'}</td>
                      )}
                      {type === 'paid' && (
                        <>
                          <td className="fw-bold text-success">₹{student.amount || '0'}</td>
                          <td className="text-primary">{student.payment_id || 'N/A'}</td>
                          <td>{student.paid_on || 'N/A'}</td>
                          {/* <td>{student.remarks || 'N/A'}</td> */}
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <h4 className="text-muted">No {type} students found</h4>
              <p className="text-muted">
                There are no {type.toLowerCase()} students for the selected criteria.
              </p>
            </div>
          )}

          <div className="mt-4 p-3 bg-light rounded">
            <h6 className="fw-bold">Course Information:</h6>
            <Row>
              <Col md={3}>
                <strong>Course:</strong> {isOverall ? paymentData.course : selectedCourse?.course_name}
              </Col>
              <Col md={3}>
                <strong>Technology:</strong> {isOverall ? 'All Technologies' : (tech.technology_name || 'N/A')}
              </Col>
              <Col md={3}>
                <strong>Passout Year:</strong> {isOverall ? paymentData.passout_year : selectedPassoutYear}
              </Col>
              <Col md={3}>
                <strong>Term:</strong> {isOverall ? paymentData.term : selectedTerms[tech.technology_id]}
              </Col>
            </Row>
          </div>
        </ModalBody>
      </Modal>
    );
  };

  // Color palette for course cards
  const courseCardColors = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
    "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
  ];

  return (
    <div className="container-fluid p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <div className="text-center mb-5">
        <p className="lead text-muted">
         Course Technologies & Payment Status
        </p>
      </div>

      {/* Courses Section */}
      <div className="mb-5">
        <Card className="shadow border-0">
          <CardBody className="p-4">
            <h4 className="mb-4 fw-bold text-center" style={{ color: "#2c3e50" }}>
              Select Course
            </h4>

            {coursesLoader ? (
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100px" }}>
                <MutatingDots
                  visible
                  height="80"
                  width="80"
                  color="#667eea"
                  secondaryColor="#667eea"
                  radius="12.5"
                  ariaLabel="mutating-dots-loading"
                />
              </div>
            ) : (
              <Row className="g-4">
                {courses.map((course, index) => (
                  <Col xl={2} lg={4} md={6} key={course._id}>
                    <Card
                      className="course-card"
                      style={{
                        background: courseCardColors[index % courseCardColors.length],
                        color: "white",
                        borderRadius: "15px",
                        boxShadow: selectedCourse?._id === course._id
                          ? "0 8px 25px rgba(102, 126, 234, 0.4)"
                          : "0 4px 15px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        border: selectedCourse?._id === course._id ? "3px solid #667eea" : "none"
                      }}
                      onClick={() => handleCourseSelect(course)}
                    >
                      <CardBody className="text-center p-4">
                        <div className="course-icon mb-3">
                          <i className="fas fa-graduation-cap fa-2x opacity-75"></i>
                        </div>
                        <h5 className="fw-bold mb-2">
                          {course.course_name}
                        </h5>
                        <small className="opacity-75">
                          {selectedCourse?._id === course._id ? "✓ Selected" : "Click to select"}
                        </small>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Passout Year Selection */}
      {selectedCourse && (
        <div className="mb-5">
          <Card className="shadow border-0">
            <CardBody className="p-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <h5 className="fw-bold mb-0" style={{ color: "#2c3e50" }}>
                    Selected Course:{" "}
                    <Badge color="primary" className="fs-6">
                      {selectedCourse.course_name}
                    </Badge>
                  </h5>
                </div>
                <div className="col-md-6">
                  <div className="text-md-end">
                    <Dropdown isOpen={yearDropdownOpen} toggle={toggleYearDropdown}>
                      <DropdownToggle
                        caret
                        className="px-4 py-2 fw-bold border-0"
                        style={{
                          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "10px",
                          minWidth: "200px"
                        }}
                      >
                        {selectedPassoutYear ? `Passout: ${selectedPassoutYear}` : "Select Passout Year"}
                      </DropdownToggle>
                      <DropdownMenu style={{ minWidth: "200px" }}>
                        {passoutYears.map((year) => (
                          <DropdownItem
                            key={year}
                            onClick={() => handlePassoutYearSelect(year)}
                            className="text-center fw-bold py-2"
                          >
                            {year}
                          </DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Main Tabs (Technologies vs Overall Course) */}
      {selectedCourse && selectedPassoutYear && (
        <div className="mb-4">
          <Card className="shadow border-0">
            <CardBody className="p-0">
              <Nav tabs className="px-3 pt-3">
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: mainActiveTab === 'technologies',
                      'cursor-pointer': true
                    })}
                    onClick={() => setMainActiveTab('technologies')}
                    style={{
                      fontWeight: 'bold',
                      color: mainActiveTab === 'technologies' ? '#fff' : '#667eea',
                      backgroundColor: mainActiveTab === 'technologies' ? '#667eea' : 'transparent'
                    }}
                  >
                    <i className="fas fa-laptop-code me-2"></i>
                    Technologies
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: mainActiveTab === 'overall',
                      'cursor-pointer': true
                    })}
                    onClick={() => setMainActiveTab('overall')}
                    style={{
                      fontWeight: 'bold',
                      color: mainActiveTab === 'overall' ? '#fff' : '#28a745',
                      backgroundColor: mainActiveTab === 'overall' ? '#28a745' : 'transparent'
                    }}
                  >
                    <i className="fas fa-chart-bar me-2"></i>
                    Overall Course
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={mainActiveTab} className="p-3">
                <TabPane tabId="technologies">
                  {/* Technologies Loading */}
                  {technologiesLoading && (
                    <div className="d-flex justify-content-center align-items-center my-5 py-5">
                      <div className="text-center">
                        <MutatingDots
                          height="100"
                          width="100"
                          color="#667eea"
                          secondaryColor="#764ba2"
                          radius="12.5"
                          ariaLabel="mutating-dots-loading"
                        />
                        <p className="mt-3 text-muted fw-bold">Loading technologies and payment data...</p>
                      </div>
                    </div>
                  )}

                  {/* Technologies Section */}
                  {technologies.length > 0 && !technologiesLoading && (
                    <div className="mt-4">
                      <Row className="g-4">
                        {technologies.map((tech, index) => (
                          <Col xl={6} lg={6} md={12} key={tech.technology_id}>
                            <Card className="shadow-sm border-0 h-100">
                              <CardBody className="p-4">
                                {/* Technology Header */}
                                <div className="d-flex align-items-center mb-4">
                                  <div className="tech-icon-wrapper me-3">
                                    <img
                                      src={`${backendFileUrl}technology-icons/${tech.technology_icon}`}
                                      alt={tech.technology_name}
                                      className="tech-icon"
                                      style={{
                                        width: "60px",
                                        height: "60px",
                                        objectFit: "contain",
                                        borderRadius: "12px",
                                        backgroundColor: "rgba(102, 126, 234, 0.1)",
                                        padding: "8px"
                                      }}
                                      onError={(e) => {
                                        e.target.src = noimage;
                                      }}
                                    />
                                  </div>
                                  <div className="flex-grow-1">
                                    <h5 className="fw-bold text-dark mb-1">
                                      {tech.technology_name}
                                    </h5>
                                    <Badge color="light" className="text-dark">
                                      {tech.terms.length} Terms Available
                                    </Badge>
                                  </div>
                                </div>

                                {/* Terms Dropdown */}
                                <div className="mb-4">
                                  <label className="form-label fw-bold text-muted mb-2">Select Term:</label>
                                  <Dropdown
                                    isOpen={termsDropdownOpen[tech.technology_id] || false}
                                    toggle={() => toggleTermsDropdown(tech.technology_id)}
                                  >
                                    <DropdownToggle
                                      caret
                                      className="w-100 py-2 fw-bold border-0"
                                      style={{
                                        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                                        color: "#2c3e50",
                                        borderRadius: "8px",
                                        border: "1px solid #dee2e6"
                                      }}
                                    >
                                      {selectedTerms[tech.technology_id] || "Select Term"}
                                    </DropdownToggle>
                                    <DropdownMenu className="w-100">
                                      {tech.terms.map((term, termIndex) => (
                                        <DropdownItem
                                          key={termIndex}
                                          onClick={() => handleTermSelect(tech.technology_id, term)}
                                          className="fw-bold"
                                        >
                                          {term}
                                        </DropdownItem>
                                      ))}
                                    </DropdownMenu>
                                  </Dropdown>
                                </div>

                                {/* Paid/Unpaid Counts - Always show when term is selected */}
                                {selectedTerms[tech.technology_id] && (
                                  <div className="mb-4">
                                    <div className="row text-center">
                                      <div className="col-6">
                                        <div className="border-end">
                                          <div className="p-3 rounded" style={{ backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
                                            <h4 className="text-success fw-bold mb-1">
                                              {paidUnpaidCounts[tech.technology_id]?.paid || 0}
                                            </h4>
                                            <small className="text-muted fw-bold">Paid Students</small>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-6">
                                        <div className="p-3 rounded" style={{ backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
                                          <h4 className="text-danger fw-bold mb-1">
                                            {paidUnpaidCounts[tech.technology_id]?.unpaid || 0}
                                            </h4>
                                          <small className="text-muted fw-bold">Unpaid Students</small>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Payment Status Tabs */}
                                {selectedTerms[tech.technology_id] && (
                                  <div>
                                    <Nav tabs className="mb-3">
                                      <NavItem>
                                        <NavLink
                                          className={classnames({
                                            active: activeTab[tech.technology_id] === 'paid',
                                            'cursor-pointer': true
                                          })}
                                          onClick={() => handleTabChange(tech.technology_id, 'paid')}
                                          style={{
                                            fontWeight: 'bold',
                                            color: activeTab[tech.technology_id] === 'paid' ? '#fff' : '#667eea',
                                            backgroundColor: activeTab[tech.technology_id] === 'paid' ? '#667eea' : 'transparent'
                                          }}
                                        >
                                          Paid Students ({paidUnpaidCounts[tech.technology_id]?.paid || 0})
                                        </NavLink>
                                      </NavItem>
                                      <NavItem>
                                        <NavLink
                                          className={classnames({
                                            active: activeTab[tech.technology_id] === 'unpaid',
                                            'cursor-pointer': true
                                          })}
                                          onClick={() => handleTabChange(tech.technology_id, 'unpaid')}
                                          style={{
                                            fontWeight: 'bold',
                                            color: activeTab[tech.technology_id] === 'unpaid' ? '#fff' : '#dc3545',
                                            backgroundColor: activeTab[tech.technology_id] === 'unpaid' ? '#dc3545' : 'transparent'
                                          }}
                                        >
                                          Unpaid Students ({paidUnpaidCounts[tech.technology_id]?.unpaid || 0})
                                        </NavLink>
                                      </NavItem>
                                    </Nav>

                                    <TabContent activeTab={activeTab[tech.technology_id]}>
                                      <TabPane tabId="paid">
                                        {paymentLoading[tech.technology_id] ? (
                                          <div className="text-center py-4">
                                            <MutatingDots
                                              height="60"
                                              width="60"
                                              color="#667eea"
                                              secondaryColor="#667eea"
                                              radius="12.5"
                                              ariaLabel="mutating-dots-loading"
                                            />
                                          </div>
                                        ) : (
                                          renderStudentTable(tech.technology_id, 'paid')
                                        )}
                                      </TabPane>
                                      <TabPane tabId="unpaid">
                                        {paymentLoading[tech.technology_id] ? (
                                          <div className="text-center py-4">
                                            <MutatingDots
                                              height="60"
                                              width="60"
                                              color="#667eea"
                                              secondaryColor="#667eea"
                                              radius="12.5"
                                              ariaLabel="mutating-dots-loading"
                                            />
                                          </div>
                                        ) : (
                                          renderStudentTable(tech.technology_id, 'unpaid')
                                        )}
                                      </TabPane>
                                    </TabContent>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  )}
                </TabPane>

                <TabPane tabId="overall">
                  {renderOverallCourseTab()}
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Full Screen Modal */}
      {renderFullScreenTable()}

      {/* Custom CSS */}
      <style jsx>{`
        .course-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
        }
        .course-card {
          transition: all 0.3s ease !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default CourseCards;
